"use client";

import { useState, useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { GoogleOAuthProvider } from "@react-oauth/google";
import Footer from "src/components/common/Footer";
import Navbar from "src/components/common/Navbar";
import TranslationProvider from "src/components/common/TranslationProvider";
import { AuthAlertProvider } from "src/contexts/AuthAlertContext";
import Loader from "src/components/common/Loader";
import getAuthToken from "src/utils/getAuthToken";

interface SessionWrapperProps {
  children: React.ReactNode;
  params?: { locale: string };
}

export default function SessionWrapper({ children }: SessionWrapperProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Static public routes (accessible without authentication)
  const publicRoutes = [
    "/",
    "/about",
    "/contact",
    "/privacy-policy",
    "/terms-condition",
    "/return-refund-policy",
    "/about-us",
    "/most-popular",
    "/auth/login",
    "/auth/registration",
    "/auth/forget-password",
    "/auth/verification-code",
    "/seller-profile",
    "/how-it-works"
  ];

  // Regex patterns for dynamic public routes
  const publicRoutePatterns = [
    /^\/categories(\/[^/]+)?$/, // matches /categories and /categories/1
    /^\/products\/[^/]+$/, // matches /products/123
    /^\/most-popular(\/[^/]+)?$/, // Add a pattern for the "Most Popular" page
    /^\/seller-profile\/\d+$/, // Add a pattern for the "Seller Profile" route
  ];

  // Check if current path matches static or dynamic public routes
  const isPublicRoute =
    publicRoutes.includes(pathname) ||
    publicRoutePatterns.some((pattern) => pattern.test(pathname));

  // Routes where Navbar should be hidden
  const hideNavbarRoutes = [
    "/auth/login",
    "/auth/registration",
    "/auth/forget-password",
    "/auth/verification-code",
    "/chat"
  ];

  // Routes where the Footer should be hidden
  const hideFooterRoutes = ["/chat", "/auth/login"];

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      const token = getAuthToken();
      if (!token && !isPublicRoute) {
        router.push("/auth/login");
      } else {
        setIsAuthenticated(!!token);
      }
      setLoading(false);
    }, 100); // Small delay to ensure proper initialization

    return () => clearTimeout(timer);
  }, [pathname, router, isPublicRoute]);

  if (loading) return <Loader />;

  return (
    <GoogleOAuthProvider
      clientId={process.env.NEXT_PUBLIC_GOOGLE_OAUTH_CLIENT_ID || ""}
    >
      <TranslationProvider>
        <AuthAlertProvider>
          {/* Show Navbar on all pages except those in hideNavbarRoutes */}
          {!hideNavbarRoutes.includes(pathname) && <Navbar />}
          {/* Render children if authenticated or on a public route */}
          {isAuthenticated || isPublicRoute ? children : null}
          {/* Show Footer on all pages except those in hideFooterRoutes */}
          {!hideFooterRoutes.includes(pathname) && <Footer />}
        </AuthAlertProvider>
      </TranslationProvider>
    </GoogleOAuthProvider>
  );
}
